/**
 * Time Role - Format Pillar
 *
 * Formatação de valores de tempo para exibição.
 *
 * @example
 * import { formatTime, getTimeSymbol, getTimeName } from '@attrx/role-morphic/time/format';
 *
 * formatTime('hour', 2.5);                     // "2.5 h"
 * formatTime('hour', 2.5, { verbose: true });  // "2.5 hours"
 * getTimeSymbol('minute');                     // "min"
 * getTimeName('hour', 2);                      // "hours"
 */

import { TIME_UNITS, type TimeUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface TimeFormatOptions {
  /** Número de casas decimais */
  decimals?: number;
  /** Usar nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Locale para formatação numérica */
  locale?: string;
  /** Notação: standard, scientific, compact */
  notation?: "standard" | "scientific" | "compact";
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor de tempo para exibição
 *
 * @param variant - Unidade
 * @param value - Valor numérico
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatTime('hour', 2.5);                     // "2.5 h"
 * formatTime('hour', 2.5, { verbose: true });  // "2.5 hours"
 * formatTime('second', 1, { verbose: true });  // "1 second"
 * formatTime('millisecond', 100, { decimals: 0 }); // "100 ms"
 */
export function formatTime(
  variant: TimeUnit,
  value: number,
  options: TimeFormatOptions = {},
): string {
  const config = TIME_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown time unit: ${variant}`);
  }

  const { decimals, verbose = false, locale, notation = "standard" } = options;

  // Format number
  let formattedNumber: string;

  if (locale || notation !== "standard") {
    const intlOptions: Intl.NumberFormatOptions = { notation };
    if (decimals !== undefined) {
      intlOptions.minimumFractionDigits = decimals;
      intlOptions.maximumFractionDigits = decimals;
    }
    formattedNumber = new Intl.NumberFormat(locale, intlOptions).format(value);
  } else if (decimals !== undefined) {
    formattedNumber = value.toFixed(decimals);
  } else {
    // Smart formatting: remove trailing zeros
    formattedNumber = Number(value.toPrecision(12)).toString();
  }

  // Get unit label
  const unitLabel = verbose
    ? value === 1
      ? config.singular
      : config.plural
    : config.symbol;

  return `${formattedNumber} ${unitLabel}`;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Retorna o símbolo de uma unidade
 *
 * @example
 * getTimeSymbol('minute');  // "min"
 * getTimeSymbol('hour');    // "h"
 */
export function getTimeSymbol(variant: TimeUnit): string {
  const config = TIME_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown time unit: ${variant}`);
  }
  return config.symbol;
}

/**
 * Retorna o nome de uma unidade (singular ou plural)
 *
 * @param variant - Unidade
 * @param count - Quantidade para determinar singular/plural (default: 1)
 *
 * @example
 * getTimeName('hour', 1);  // "hour"
 * getTimeName('hour', 2);  // "hours"
 */
export function getTimeName(variant: TimeUnit, count: number = 1): string {
  const config = TIME_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown time unit: ${variant}`);
  }
  return count === 1 ? (config.singular || config.symbol) : (config.plural || config.symbol);
}
